import { isAxiosError } from "axios";
import {
  createPriceBook,
  duplicatePriceBook,
  updatePriceBook,
} from "../../../../services";
import { pushGlobalSuccess } from "../../../../success/globalSuccess";
import { PricebookValidity } from "../../../../types/price-book.type";
import { PriceBookDrawerMode, PriceBookFormState } from "../types";
import {
  CODE_TAKEN_RETRY,
  DUPLICATE_FAILED,
  PRICE_BOOK_SAVED_NOTICE,
  SAVE_FAILED,
} from "./formState";
import { buildValidityRequest, isValidityChanged } from "./validityForm";

/** Status the API refuses a create whose code is no longer free with. */
const HTTP_CONFLICT = 409;

export interface SavePriceBookInput {
  mode: PriceBookDrawerMode;
  /** The pricebook being edited, or the source being copied. Empty when creating. */
  sourceUuid: string;
  form: PriceBookFormState;
  /** The edited book's validity as loaded; null when creating or copying. */
  storedValidity: PricebookValidity | null;
}

export interface SavePriceBookOutcome {
  /** The pricebook this save wrote; empty when nothing reached the API. */
  uuid: string;
  /** Thai reason the save stopped; empty when it landed. */
  errorMessage: string;
  /**
   * True when the create was refused because its code is no longer free, so the caller
   * has to put a new code in the form before the user can save again.
   */
  codeTaken: boolean;
}

const buildHeaderPayload = (form: PriceBookFormState) => ({
  name: form.name.trim(),
  type: form.type,
  status: form.status,
});

/**
 * One request per mode: the book % is only set here on a new book or a copy. A new book and a copy
 * always carry their validity; an edit only when it moved.
 */
const writePriceBook = async ({
  mode,
  sourceUuid,
  form,
  storedValidity,
}: SavePriceBookInput): Promise<string> => {
  const discountPercent = Number(form.discountPercent);
  if (mode === "create") {
    const {
      data: { data },
    } = await createPriceBook({
      code: form.code.trim(),
      ...buildHeaderPayload(form),
      discount_percent: discountPercent,
      ...buildValidityRequest(form),
    });
    return data.uuid;
  }
  if (mode === "duplicate") {
    const {
      data: { data },
    } = await duplicatePriceBook(sourceUuid, {
      ...buildHeaderPayload(form),
      discount_percent: discountPercent,
      ...buildValidityRequest(form),
    });
    return data.uuid;
  }
  const isValidityMoved =
    storedValidity !== null && isValidityChanged(form, storedValidity);
  await updatePriceBook(sourceUuid, {
    ...buildHeaderPayload(form),
    ...(isValidityMoved ? buildValidityRequest(form) : {}),
  });
  return sourceUuid;
};

/** Saves the drawer and announces it once; a refused save says why in the drawer instead. */
export const savePriceBook = async (
  input: SavePriceBookInput,
): Promise<SavePriceBookOutcome> => {
  try {
    const uuid = await writePriceBook(input);
    pushGlobalSuccess(PRICE_BOOK_SAVED_NOTICE);
    return { uuid, errorMessage: "", codeTaken: false };
  } catch (error) {
    // The axios interceptor already showed the API's own wording. A create is the only write
    // that carries a code, so a 409 there means the prefilled code was claimed first.
    const codeTaken =
      input.mode === "create" &&
      isAxiosError(error) &&
      error.response?.status === HTTP_CONFLICT;
    if (codeTaken) return { uuid: "", errorMessage: CODE_TAKEN_RETRY, codeTaken };
    const errorMessage = input.mode === "duplicate" ? DUPLICATE_FAILED : SAVE_FAILED;
    return { uuid: "", errorMessage, codeTaken: false };
  }
};
